import React from "react";
import {
  Card,
  Text,
  Avatar,
  Stack,
  Divider,
  ScrollArea,
  createStyles,
  Center,
  Loader,
  Box,
  Button,
  Title,
  Badge,
  Group,
} from "@mantine/core";
import { useGetNotifications } from "api/notifications/useNotifications";
import { useNavigate } from "react-router-dom";
import { IconBell, IconCheck, IconX } from "@tabler/icons-react";

const useStyles = createStyles((theme) => ({
  card: {
    borderRadius: 8,
    padding: 12,
    border: `1px solid ${theme.colors.gray[3]}`,
    background: theme.white,
    boxShadow: "0 6px 18px rgba(12,20,40,0.04)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 8px",
    marginBottom: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: 800,
    color: theme.colors.dark[8],
  },
  viewAllBtn: {
    height: "34px",
    borderRadius: "8px",
    padding: "6px 12px",
  },
  list: {
    marginTop: 8,
  },
  row: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    padding: "16px 12px",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.colors.gray[0],
    },
  },
  unreadRow: {
    backgroundColor: theme.colors.blue[0],
    "&:hover": {
      backgroundColor: theme.colors.blue[1],
    },
  },
  textWrap: {
    minWidth: 0,
    flex: 1,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.dark[9],
    marginBottom: 4,
    wordBreak: "break-word",
  },
  secondaryText: {
    fontSize: 12,
    color: theme.colors.gray[6],
    marginBottom: 4,
  },
  timeText: {
    fontSize: 11,
    color: theme.colors.gray[5],
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.colors.blue[6],
    marginLeft: 8,
  },
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: theme.colors.gray[1],
  },
  actionButtons: {
    display: "flex",
    gap: 8,
    marginTop: 8,
  },
  actionButton: {
    padding: "4px 12px",
    height: "auto",
    fontSize: 12,
  },
}));

type NotificationItem = {
  id: string;
  title?: string;
  description?: string;
  message?: string;
  userName?: string;
  user?: {
    name?: string;
    avatar?: string;
  };
  avatar?: string | null;
  createdAt?: string;
  timestamp?: string;
  read?: boolean;
  type?: string;
  link?: string;
};

interface DirectorNotificationsProps {
  limit?: number;
}

function getInitialsFromName(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function isUrl(value?: string | null): boolean {
  if (!value) return false;
  return (
    /^(https?:)?\/\//.test(value) ||
    /\.(png|jpg|jpeg|svg|gif)(\?|$)/i.test(value)
  );
}

function extractNotificationsFromPage(page: unknown): NotificationItem[] {
  const p = page as Record<string, unknown>;

  if (Array.isArray(p)) {
    return p as NotificationItem[];
  }

  if (p && typeof p === "object") {
    if (Array.isArray(p.notifications)) {
      return p.notifications as NotificationItem[];
    }
    if (Array.isArray(p.data)) {
      return p.data as NotificationItem[];
    }
    if (Array.isArray(p.items)) {
      return p.items as NotificationItem[];
    }
    if (Array.isArray(p.results)) {
      return p.results as NotificationItem[];
    }
    if (Array.isArray(p.payload)) {
      return p.payload as NotificationItem[];
    }
    if (
      p.data &&
      typeof p.data === "object" &&
      Array.isArray((p.data as Record<string, unknown>).notifications)
    ) {
      return (p.data as Record<string, unknown>)
        .notifications as NotificationItem[];
    }
    if (
      p.notifications &&
      typeof p.notifications === "object" &&
      Array.isArray((p.notifications as Record<string, unknown>).data)
    ) {
      return (p.notifications as Record<string, unknown>)
        .data as NotificationItem[];
    }

    // Check all object keys for arrays
    const keys = Object.keys(p);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (Array.isArray(p[key])) {
        return p[key] as NotificationItem[];
      }
    }
  }

  return [];
}

function formatTimeAgo(dateString?: string): string {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    }
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? "s" : ""} ago`;
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getNotificationIcon(type?: string) {
  switch (type?.toLowerCase()) {
    case "leave":
    case "leave_request":
      return <IconBell size={20} color="#228be6" />;
    case "attendance":
      return <IconBell size={20} color="#40c057" />;
    case "alert":
      return <IconBell size={20} color="#fa5252" />;
    default:
      return <IconBell size={20} color="#868e96" />;
  }
}

export function DirectorNotifications({
  limit = 5,
}: DirectorNotificationsProps) {
  const { classes, cx } = useStyles();
  const { data, isLoading, isError } = useGetNotifications();
  const navigate = useNavigate();

  const apiList: NotificationItem[] = React.useMemo(() => {
    if (!data) return [];

    const { pages } = data as { pages?: unknown[] };
    if (!pages || !Array.isArray(pages)) return [];

    return pages.flatMap((p) => extractNotificationsFromPage(p));
  }, [data]);

  // Show only first 'limit' notifications
  const displayedList = React.useMemo(
    () => apiList.slice(0, limit),
    [apiList, limit],
  );

  const unreadCount = React.useMemo(
    () => apiList.filter((n) => !n.read).length,
    [apiList],
  );

  if (isLoading) {
    return (
      <Card className={classes.card} radius="md">
        <Center style={{ height: 400 }}>
          <Loader />
        </Center>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className={classes.card} radius="md">
        <Box p="md">
          <Text weight={700} color="red" mb="xs">
            Unable to load notifications
          </Text>
          <Text size="sm" color="dimmed">
            Please try again later
          </Text>
        </Box>
      </Card>
    );
  }

  return (
    <Card className={classes.card} radius="md">
      <div className={classes.header}>
        <Group spacing="xs">
          <Title order={3} style={{ fontSize: "18px" }}>
            Notifications
          </Title>
          {unreadCount > 0 && (
            <Badge color="blue" size="sm">
              {unreadCount}
            </Badge>
          )}
        </Group>
        {/* <Button
          variant="light"
          className={classes.viewAllBtn}
          onClick={() => navigate("/notifications")}
        >
          View All
        </Button> */}
      </div>

      <Divider />

      <ScrollArea style={{ height: 450, marginTop: 8 }} type="auto">
        <Stack spacing={0} className={classes.list}>
          {displayedList.length > 0 ? (
            displayedList.map((notification: NotificationItem) => {
              const avatarVal =
                notification.avatar || notification.user?.avatar;
              const showAsImage = isUrl(avatarVal);
              const initials = getInitialsFromName(
                notification.userName || notification.user?.name,
              );
              const timeAgo = formatTimeAgo(
                notification.createdAt || notification.timestamp,
              );
              const title =
                notification.title ||
                notification.description ||
                notification.message;
              const isUnread = !notification.read;

              return (
                <div key={notification.id}>
                  <div
                    className={cx(classes.row, {
                      [classes.unreadRow]: isUnread,
                    })}
                  >
                    <Avatar
                      src={showAsImage ? avatarVal! : undefined}
                      radius="xl"
                      size="md"
                    >
                      {!showAsImage ? initials : null}
                    </Avatar>

                    <div className={classes.textWrap}>
                      <Group position="apart" spacing={4}>
                        <Text className={classes.primaryText}>
                          {title || "New Notification"}
                        </Text>
                        {/* {isUnread && <div className={classes.unreadDot} />} */}
                      </Group>

                      {notification.userName && (
                        <Text className={classes.secondaryText}>
                          From: {notification.userName}
                        </Text>
                      )}

                      {notification.description &&
                        notification.description !== notification.title && (
                          <Text className={classes.secondaryText}>
                            {notification.description}
                          </Text>
                        )}

                      <Group position="apart" mt={4}>
                        <Text className={classes.timeText}>{timeAgo}</Text>
                        {/* <Group spacing={4} className={classes.actionButtons}>
                          {isUnread && (
                            <Button
                              variant="subtle"
                              size="xs"
                              color="blue"
                              compact
                              className={classes.actionButton}
                              leftIcon={<IconCheck size={12} />}
                            >
                              Mark Read
                            </Button>
                          )}
                          <Button
                            variant="subtle"
                            size="xs"
                            color="gray"
                            compact
                            className={classes.actionButton}
                            leftIcon={<IconX size={12} />}
                          >
                            Dismiss
                          </Button>
                        </Group> */}
                      </Group>
                    </div>
                  </div>
                  <Divider />
                </div>
              );
            })
          ) : (
            <Center py="xl">
              <Stack align="center" spacing="xs">
                <IconBell size={40} color="gray" opacity={0.5} />
                <Text size="lg" weight={500} color="dimmed">
                  No notifications
                </Text>
                <Text size="sm" color="dimmed">
                  You&apos;re all caught up!
                </Text>
              </Stack>
            </Center>
          )}
        </Stack>
      </ScrollArea>
    </Card>
  );
}
